/**
 * Scope Intelligence findings service
 *
 * Calls ideoz-ai-api directly via aiApi for panel load and resolve actions.
 *
 * @module @ideoz/core/api/services/findings.service
 */

import apiClient from '../client';
import { ApiUrls } from '../ApiUrls';
import type {
  FindingAction,
  FindingsPanelResponse,
  ResolveFindingResponse,
} from '../../types/findings.types';

export const findingsService = {
  /**
   * Load (or refresh) the Scope Intelligence panel for a scope.
   * Also enqueues live processing asynchronously on the server.
   */
  async getScopeFindings(
    scopeId: string,
    includeDismissed: boolean = false
  ): Promise<FindingsPanelResponse> {
    const response = await apiClient.get<FindingsPanelResponse>(
      ApiUrls.getFindingsByScope(scopeId, includeDismissed)
    );
    return response.data;
  },

  /**
   * Apply a panel action. Contradiction findings use `reaffirm` / `correct`
   * (ADR-043) instead of `confirm`; other types use
   * `confirm` / `dismiss` / `investigate` / `validate` / `mark_untested`.
   */
  async resolveFinding(
    findingId: string,
    action: FindingAction,
    userId: string
  ): Promise<ResolveFindingResponse> {
    const response = await apiClient.post<ResolveFindingResponse>(
      ApiUrls.resolveFinding(findingId),
      { action, user_id: userId }
    );
    return response.data;
  },
};

export default findingsService;
